import React, { useEffect, useState } from "react";
import html2canvas from 'html2canvas';
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Triangle } from 'react-loader-spinner';
import { jwtDecode } from 'jwt-decode';

import styles from "../styles/resultQuestionary.module.css";

import { logoutUser } from "../logoutUser";

import { userLogged } from "../userLogged";
import { verifyUserPaid } from "../verifyUserPaid";
import { ProfileRadarChart } from "../components/ProfileRadarChart";
import axios from "axios";
import { urlAPI_Current } from "../config";
import { Menu } from "../components/Menu";
import { TriangularRadarChart } from "../components/TriangularRadarChart";
import useAccessTracking from "../components/useAccessTracking";
import { verifyAuthToken } from "../verifyAuthToken";
import { ProfileColumnsChart } from "../components/ProfileColumnsChart";

export function ResultQuestionary() {
    const [userData, setUserData] = useState([]);
    const [profileResult, setProfileResult] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [remainingTime, setRemainingTime] = useState(null);
    const navigateTo = useNavigate();

    const [darkMode, setDarkMode] = useState(() => 
        window.localStorage.getItem('darkMode') === 'true'
    );

    useAccessTracking();

    async function generateChartImages() {
        try {
            await new Promise(resolve => setTimeout(resolve, 2000));

            const chartImages = [
                { id: 'invisible-chart', type: 'chartImageRadar' },
                { id: 'invisible-chart-2', type: 'chartImageTriangulo' },
                { id: 'invisible-chart-3', type: 'chartImageColunas' }
            ];

            const imagePromises = chartImages.map(async ({ id, type }) => {
                const chartElement = document.getElementById(id);
                if (chartElement) {
                    const canvas = await html2canvas(chartElement, {
                        useCORS: true,
                        backgroundColor: null,
                        scale: 2
                    });
                    const imageData = canvas.toDataURL('image/png');

                    return {
                        chartImage: imageData,
                        userId: localStorage.getItem('userId'),
                        type
                    };
                }
            });

            const imagesData = await Promise.all(imagePromises);
            const filteredImagesData = imagesData.filter(Boolean); // Remove elementos undefined

            // Enviar todas as imagens em uma única chamada
            axios.post(`${urlAPI_Current}/saveChartImage`, filteredImagesData, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
        } catch (error) {
            console.error('Erro ao gerar imagens dos gráficos:', error);
        }
    }

    async function getQuestionsAnsweredCount(user){
        const response = await axios.post(
          `${urlAPI_Current}/getQuestionsAnsweredCount`,
          {}, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`  
            }
        }
        );
        if(response.data.countQuestionsAnswered == 0){
            navigateTo("/questionaryAlert");
            return;
        } else if (response.data.countQuestionsAnswered >= 1 && response.data.countQuestionsAnswered < 12){
            navigateTo("/questionary");
            return;
        } else if (response.data.countQuestionsAnswered >= 12){
            // só vai fazer uma vez
            
            if(user.relatorioEnviado == 0){
                generateChartImages();
                generateReport(); 
            } 
        }
      }

    async function getUserLogged(){
        const user = await userLogged();
        setUserData(user)
    
        if (!user) { // não tiver usuário, ou authToken invalido
          toast.error('Faça o login primeiro');
          navigateTo('/login');
        } 
        else if(user.codTipoUsuario != 1){
          logoutUser();
          toast.error('Você não tem acesso a essa página');
          navigateTo('/login');
        } else { // loga
            const responsePaid = await verifyUserPaid();
            if(responsePaid.success == true){
                console.log(`Plano Atual ${responsePaid.plan}`);
                await getQuestionsAnsweredCount(user);
            } else {
              toast.error('Faça o pagamento primeiro');
              navigateTo("/plans");
            }
        }
    }

    // não assincrono, pois o final report demora para ser gerado
    function generateReport(){
        axios.post(`${urlAPI_Current}/generateFinalReport`, {}, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`  
            }
        })
        .then(response => {
            if(response.data.success) {
                console.log('Relatório gerado com sucesso');
            }
        })
        .catch(error => {
            console.error('Erro ao iniciar geração do relatório:', error);
        });
    }

    async function verifyTimeLastAnswer(){
        try {
            setIsLoading(true);

            const timeLastAnswer = await axios.post(`${urlAPI_Current}/getTimeLastAnswer`, {
                userId: localStorage.getItem('userId') }, 
                { 
                headers: 
                    { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                });
    
            if(timeLastAnswer.data.success == true){
                const lastAnswerDate = new Date(timeLastAnswer.data.dataGerando);
                const relatorioEnviado = timeLastAnswer.data.relatorioEnviado;

                const currentDate = new Date();
                
                // ainda não comecou a gerar o relatório
                if(timeLastAnswer.data.dataGerando == null){
                    setIsLoading(true);
                    setRemainingTime(0); 
                    return false;
                }
                const diffInMinutes = Math.floor((currentDate - lastAnswerDate) / (1000 * 60));
                
                // se não gerou, ou se tempo não passou 30 min
                if(relatorioEnviado == 0 || diffInMinutes < 30) {
                    const minutesRemaining = 30 - diffInMinutes;
                    setRemainingTime(diffInMinutes);
                    toast.error(`Por favor, aguarde um momento para processar o relatório`);
                    setIsLoading(true);
                    return false;
                } else {
                    setIsLoading(false);
                    toast.success(`Relatório gerado com sucesso!`);
                    return true;
                }
            }
        } catch (error) {
            console.error("Erro ao verificar tempo:", error);
            return false;
        }
    }

    async function downloadFinalReportPdf(codCliente, nomeCliente) {
        try {
          toast.info('Iniciando download do relatório...');
      
          const response = await axios.post(
            `${urlAPI_Current}/downloadReportPDF`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
              },
              responseType: 'blob'
            }
          );
      
          // Cria um link temporário para download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `relatorio-${nomeCliente}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
      
          toast.success('Relatório baixado com sucesso!');
        } catch (error) {
          console.error('Erro ao baixar o PDF:', error);
          toast.error('Relatório não encontrado. Tente novamente');
        }
      }
    useEffect(() => {
        window.localStorage.setItem('darkMode', darkMode);
        document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        verifyTimeLastAnswer();
        getUserLogged();
        verifyTimeLastAnswer();

        const pollingInterval = setInterval(() => {
            verifyTimeLastAnswer();
        }, 60000); 

        return () => clearInterval(pollingInterval);
    }, [darkMode]); 

    useEffect(() => {
        verifyAuthToken(navigateTo);
    }, []);

    return (
        <div className={styles.container}>
            <Menu darkMode={darkMode} />
                <div 
                    id="invisible-chart" 
                    style={{ 
                        position: 'absolute', 
                        left: '-9999px', 
                        top: '-9999px',
                        width: '600px',
                        height: '400px'
                    }}
                >
                    <ProfileRadarChart needShowPontuation={true}/>
                </div>

                <div 
                    id="invisible-chart-2" 
                    style={{ 
                        position: 'absolute', 
                        left: '-9999px', 
                        top: '-9999px',
                        width: '630px',
                        height: '420px'
                    }}
                >
                    <TriangularRadarChart userId={userData.codUsuario}/>
                </div>

                <div 
                    id="invisible-chart-3" 
                    style={{ 
                        position: 'absolute', 
                        left: '-9999px', 
                        top: '-9999px',
                        width: '900px',
                        height: '600px'
                    }}
                >
                    <ProfileColumnsChart />
                </div>

                <div className={styles.divResults}>
                    {isLoading ? (
                        <div className={styles.loadingContainer}>
                            <Triangle
                                visible={true}
                                height="80"
                                width="80"
                                color="#a259d1"
                                ariaLabel="triangle-loading"
                            />
                            <br />

                            <h3>Processando dados...</h3>

                            <br />

                            <p>Estamos gerando seu relatório. Esse processo pode levar entre 30 e 40 minutos.</p>
                            <p>Assim que estiver pronto, você será avisado para o email: "{userData.email}"</p>   
                            <p><strong className={styles.textClosed}>Você pode fechar esta tela e voltar depois</strong> para <small className={styles.underlineText}>baixar relatório.pdf</small> </p>
                            {remainingTime ? (
                                <p>Tempo decorrido: {remainingTime} minutos</p>
                            ) : null}
                        </div>
                    ) : (
                        <div className={styles.reportContainer}>
                            <div className={styles.reportMessage}>
                                <h2>Seu Relatório EVOK™ está pronto!</h2>
            
                                <div className={styles.reportDetails}>
                                    <span>Parabéns! Concluímos a análise completa do seu perfil, incluindo:</span>
                                    <ul>
                                        <li>Diagnóstico detalhado das suas 3 dimensões de performance</li>
                                        <li>Análise do seu estilo e perfil</li>
                                        <li>Mapeamento das suas habilidades essenciais</li>
                                        <li>Recomendações personalizadas para desenvolvimento</li>
                                    </ul>

                                    <p className={styles.highlightText}>
                                        Baixe agora seu relatório personalizado e descubra insights valiosos sobre seu perfil profissional.
                                    </p>
                                </div>

                                <div className={styles.buttonContainer}>
                                    <button 
                                        className={styles.downloadButton}
                                        onClick={() => downloadFinalReportPdf(userData.codUsuario, userData.nome)}
                                    >
                                        Baixar Relatório PDF
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
        </div>
    );
}